import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchCurrentPlan,
  fetchSubscription,
  fetchPaymentHistory,
  initiatePayment,
  cancelSubscription,
} from "../../redux/thunks/paymentThunks";
import {
  selectCurrentPlan,
  selectQuotas,
  selectSubscription,
  selectPaymentHistory,
  selectCheckout,
} from "../../redux/selectors/paymentSelectors";
import { clearCheckout } from "../../redux/slices/paymentSlice";
import { formatAmount, formatQuota } from "../../config/plans.config";
import "./PaymentBoard.css";

// Locale used for date formatting (ar-TN keeps Latin digits, as is usual in Tunisia)
const DATE_LOCALES = { fr: "fr-FR", ar: "ar-TN", en: "en-GB" };

const formatDate = (value, lng) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const base = (lng || "en").split("-")[0];
  return d.toLocaleDateString(DATE_LOCALES[base] || lng);
};

// Only the quota field names live here; the labels are in the JSON files.
const QUOTA_FIELDS = [
  "medicationCount",
  "patientCount",
  "fileShareMonthly",
  "relativeCount",
  "aiAssistantMonthly",
  "storageMb",
];

export default function PaymentBoard({
  plans,
  userId,
  userType = "patient",
  title,
  subtitle,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language;
  const dispatch = useDispatch();

  const currentPlan = useSelector(selectCurrentPlan);
  const quotas = useSelector(selectQuotas);
  const subscription = useSelector(selectSubscription);
  const history = useSelector(selectPaymentHistory);
  const checkout = useSelector(selectCheckout);

  const [selectedCycle, setSelectedCycle] = useState("monthly");

  useEffect(() => {
    if (!userId) return;
    dispatch(fetchCurrentPlan(userId));
    dispatch(fetchSubscription(userId));
    dispatch(fetchPaymentHistory(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (checkout.url) {
      window.location.href = checkout.url;
    }
  }, [checkout.url]);

  useEffect(() => () => dispatch(clearCheckout()), [dispatch]);

  const handleSubscribe = (planKey) => {
    if (!userId || planKey === currentPlan) return;
    dispatch(
      initiatePayment({ userId, plan: planKey, billingCycle: selectedCycle, userType })
    );
  };

  const handleCancel = () => {
    if (!userId) return;
    if (!window.confirm(t("payment.confirmCancel"))) return;
    dispatch(cancelSubscription(userId));
  };

  // --- Plan content: translated via JSON, falls back to the values from plans.config ---
  // Patient and doctor plans can share a key (e.g. "pro"), so translations are scoped.
  const planScope = userType === "patient" ? "patient" : "doctor";
  const planName = (plan) =>
    t(`plans.${planScope}.${plan.key}.name`, { defaultValue: plan.name });

  const planTagline = (plan) =>
    t(`plans.${planScope}.${plan.key}.tagline`, { defaultValue: plan.tagline });

  const planFeatures = (plan) => {
    const features = t(`plans.${planScope}.${plan.key}.features`, {
      returnObjects: true,
      defaultValue: plan.features,
    });
    return Array.isArray(features) ? features : plan.features || [];
  };

  // --- Quotas ---
  const renderQuota = (field, value) => {
    if (field === "storageMb") {
      return value === null
        ? t("payment.quota.storageUnlimited")
        : t("payment.quota.storageMb", { value: (value / 1024).toFixed(1) });
    }
    const formatted = value === null ? t("payment.quota.unlimited") : formatQuota(value);
    return t(`payment.quota.${field}`, { value: formatted });
  };

  const currentPlanObj = plans.find((p) => p.key === currentPlan);
  const isSubscriptionActive =
    subscription?.status === "active" && currentPlan !== "free" && !!currentPlan;

  // ms-payment's quota schema is patient-only (medicationCount, relativeCount, …),
  // so doctor plans show their own feature list instead of the fetched quotas.
  const quotaEntries =
    userType === "patient"
      ? QUOTA_FIELDS.filter(
          (field) => quotas && Object.prototype.hasOwnProperty.call(quotas, field)
        )
      : [];

  return (
    <div className="payment-page" dir={i18n.dir(lng)}>
      <div className="payment-header">
        <h1>{title ?? t("payment.title")}</h1>
        <p>{subtitle ?? t("payment.subtitle")}</p>
      </div>

      <div className="payment-current-card">
        <div>
          <span className="payment-current-label">{t("payment.currentPlan")}</span>
          <h2 className="payment-current-plan">
            {currentPlanObj ? planName(currentPlanObj) : t("payment.noActivePlan")}
          </h2>
          {subscription?.expiresAt && (
            <p className="payment-current-expiry">
              {subscription.status === "cancelled"
                ? t("payment.accessUntil")
                : t("payment.renewsOn")}{" "}
              {formatDate(subscription.expiresAt, lng)}
            </p>
          )}
        </div>

        {quotaEntries.length > 0 ? (
          <ul className="payment-quota-list">
            {quotaEntries.map((field) => (
              <li key={field}>{renderQuota(field, quotas[field])}</li>
            ))}
          </ul>
        ) : (
          currentPlanObj?.features && (
            <ul className="payment-quota-list">
              {planFeatures(currentPlanObj)
                .slice(0, 4)
                .map((f, i) => (
                  <li key={`${currentPlanObj.key}-${i}`}>{f}</li>
                ))}
            </ul>
          )
        )}

        {isSubscriptionActive && (
          <button
            type="button"
            className="payment-cancel-btn"
            onClick={handleCancel}
            disabled={subscription?.status === "cancelled"}
          >
            {subscription?.status === "cancelled"
              ? t("payment.cancellationScheduled")
              : t("payment.cancelSubscription")}
          </button>
        )}
      </div>

      <div
        className="payment-cycle-toggle"
        role="tablist"
        aria-label={t("payment.billingCycle")}
      >
        <button
          type="button"
          className={selectedCycle === "monthly" ? "active" : ""}
          onClick={() => setSelectedCycle("monthly")}
        >
          {t("payment.monthly")}
        </button>
        <button
          type="button"
          className={selectedCycle === "yearly" ? "active" : ""}
          onClick={() => setSelectedCycle("yearly")}
        >
          {t("payment.yearly")}
        </button>
      </div>

      {checkout.error && (
        <div className="payment-error-banner">{String(checkout.error)}</div>
      )}

      <div className="payment-plans-grid">
        {plans.map((plan) => {
          const isCurrent = plan.key === currentPlan;
          const isLoadingThis = checkout.loading && checkout.plan === plan.key;

          return (
            <div
              key={plan.key}
              className={`payment-plan-card${plan.highlighted ? " highlighted" : ""}${
                isCurrent ? " current" : ""
              }`}
            >
              {plan.highlighted && (
                <span className="payment-plan-badge">{t("payment.popular")}</span>
              )}
              <h3>{planName(plan)}</h3>
              <p className="payment-plan-tagline">{planTagline(plan)}</p>
              <div className="payment-plan-price">
                {plan.priceMillimes === 0 ? (
                  t("payment.free")
                ) : (
                  <>
                    {formatAmount(
                      selectedCycle === "yearly"
                        ? plan.priceMillimes * 12
                        : plan.priceMillimes,
                      plan.currency,
                      lng
                    )}
                    <span>
                      {selectedCycle === "yearly"
                        ? t("payment.perYear")
                        : t("payment.perMonth")}
                    </span>
                  </>
                )}
              </div>
              {plan.trialDays && !isCurrent && (
                <p className="payment-plan-trial">
                  {t("payment.trialDays", { count: plan.trialDays })}
                </p>
              )}
              <ul className="payment-plan-features">
                {planFeatures(plan).map((f, i) => (
                  <li key={`${plan.key}-${i}`}>{f}</li>
                ))}
              </ul>
              <button
                type="button"
                className="payment-plan-btn"
                disabled={isCurrent || isLoadingThis || !userId}
                onClick={() => handleSubscribe(plan.key)}
              >
                {isCurrent
                  ? t("payment.currentPlan")
                  : isLoadingThis
                  ? t("payment.redirecting")
                  : t("payment.subscribe")}
              </button>
            </div>
          );
        })}
      </div>

      <div className="payment-history-section">
        <h2>{t("payment.history.title")}</h2>
        {history.length === 0 ? (
          <p className="payment-history-empty">{t("payment.history.empty")}</p>
        ) : (
          <div className="payment-history-table-wrap">
            <table className="payment-history-table">
              <thead>
                <tr>
                  <th>{t("payment.history.date")}</th>
                  <th>{t("payment.history.plan")}</th>
                  <th>{t("payment.history.amount")}</th>
                  <th>{t("payment.history.method")}</th>
                  <th>{t("payment.history.status")}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id || row.flouciPaymentId}>
                    <td>{formatDate(row.paidAt || row.createdAt, lng)}</td>
                    <td>
                      {t(`plans.${planScope}.${row.plan}.name`, { defaultValue: row.plan })}
                    </td>
                    <td>{formatAmount(row.amountMillimes, undefined, lng)}</td>
                    <td>{row.paymentType || "—"}</td>
                    <td>
                      <span className={`payment-status payment-status--${row.status}`}>
                        {t(`payment.status.${row.status}`, { defaultValue: row.status })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}